import cookies from 'js-cookie'
import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { ERROR_ACCEPT_INVITATION } from '@/app/module/auth/definitions'
import { getUserPreferences } from '@/app/module/auth/store/session.slice'
import { createNotification } from '@/app/module/notifications/store/actions'
import { getUser } from '@/app/module/user/store/actions'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

import { selectToken } from '../store/selectors'

export type AcceptInvitationParams = {
  invitationToken: string
}

export const useAcceptInvitation = () => {
  const dispatch = useAppDispatch()
  const [inviteAccept, mutationResult] = esApi.endpoints.inviteAccept.useMutation()
  const [isLoading, setIsLoading] = useState(false)

  const token = useSelector(selectToken)

  const acceptInvitation = useCallback(
    async ({ invitationToken }: AcceptInvitationParams) => {
      if (!token) {
        // Ensure the user is logged in
        return false
      }

      setIsLoading(true)

      try {
        const response = await inviteAccept({ invtoken: invitationToken }).unwrap()

        dispatch(
          createNotification({
            'accept-invitation-success': {
              type: 'success',
              message: 'Invitation accepted successfully!',
            },
          }),
        )

        // Login after accepting invitation to get user data
        const user = await dispatch(getUser({ token }))
        if (user && !(user instanceof Error)) {
          dispatch(getUserPreferences({ user, token }))
        }

        cookies.remove('redirectURL')
        return { response, user }
      } catch (error) {
        const errorMessage = getErrorMessage(error, ERROR_ACCEPT_INVITATION)
        dispatch(
          createNotification({
            'accept-invitation-error': {
              type: 'error',
              message: errorMessage,
            },
          }),
        )
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [token, dispatch, inviteAccept],
  )

  return [acceptInvitation, { ...mutationResult, isLoading }] as const
}
